import React, { useCallback, useEffect, useState } from 'react';
import { Admin, EAdminRoles, ESnackbarStyle, SessionVrUser, TableLog, TableSession, VrUser } from 'shared/types';
import * as Styled from './styles';
import { Button, Input, List, Table, Tooltip } from 'antd';
import { SwapOutlined, CloseOutlined } from '@ant-design/icons';
import { LOG_COLUMNS } from './constants';
import { useAppDispatch, useAppSelector } from 'shared/hooks';
import { TableWrapper } from 'shared/styles';
import { getUser } from 'utils/user-utils';
import { Link } from 'react-router-dom';
import { getVrUsers } from 'services/api/vrUsersService';
import { reassignSessionUser } from 'services/api/sessionsService';
import { ConfirmModal } from 'shared/components';
import { showModal, closeModal, setIsDataLoadingParameter } from 'services/store/reducers/modalReducer';
import { openNotification } from 'utils/notification-utils';

type Props = {
  user: SessionVrUser;
  session: TableSession;
  onReassigned?: () => void;
};

const UserField: React.FC<Props> = ({ user, session, onReassigned }): JSX.Element => {
  const [tableLogs, setTableLogs] = useState<TableLog[]>([]);
  const currentUser = useAppSelector((state): Admin => state.auth.user!);
  const isSuperAdmin = currentUser?.role === EAdminRoles.SUPER_ADMIN;
  const dispatch = useAppDispatch();

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<VrUser[]>([]);
  const [searching, setSearching] = useState(false);

  const checkIsUserFromAnotherSchool = (): boolean => {
    if (currentUser.role === EAdminRoles.SUPER_ADMIN) return false;
    const email = user.email;
    // @ts-ignore
    const candidate = currentUser.school?.domain || '';
    return !(email && email.includes(candidate));
  };

  useEffect((): void => {
    setTableLogs([{
      key: session.id,
      guid: session.id,
      startedAt: session.startedAt || '',
      finishedAt: session.endedAt || '',
      duration: session.duration || '',
      roomId: session.roomId || '',
      userRole: session.userRole || '',
      user: getUser(user),
      school: session.school || '',
      dataSource: session.dataSource || ''
    }]);
  }, [user, session]);

  const handleSearch = useCallback(async (value: string) => {
    setSearchValue(value);
    if (value.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await getVrUsers(1, 10, {}, { search: value.trim() });
      setSearchResults(response.data);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSelectUser = (selectedUser: VrUser): void => {
    setShowSearch(false);
    setSearchValue('');
    setSearchResults([]);

    dispatch(
      showModal(
        <ConfirmModal
          title={`Are you sure you want to reassign this session from "${getUser(user)}" to "${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})"?`}
          confirmAction={async (): Promise<void> => {
            dispatch(setIsDataLoadingParameter(true));
            try {
              await reassignSessionUser(session.id, selectedUser.id);
              openNotification(ESnackbarStyle.SUCCESS, 'Session reassigned successfully.');
              onReassigned?.();
            } catch (err: any) {
              openNotification(ESnackbarStyle.ERROR, err?.response?.data?.message || err.message);
            } finally {
              dispatch(setIsDataLoadingParameter(false));
              dispatch(closeModal());
            }
          }}
        />
      )
    );
  };

  return (
    <Styled.UserField>
      <Styled.UserInfo $isFromAnotherSchool={checkIsUserFromAnotherSchool()}>
        <Link to={`/d/manage-users?search=${encodeURIComponent(user.email || '')}`} style={{ color: '#1890ff' }}>
          {getUser(user)}{user.email ? ` (${user.email})` : ''}
        </Link>
        {isSuperAdmin && !showSearch && (
          <Tooltip title='Change student'>
            <Button
              type='link'
              size='small'
              icon={<SwapOutlined />}
              onClick={() => setShowSearch(true)}
              style={{ marginLeft: 8, padding: 0 }}
            />
          </Tooltip>
        )}
        {isSuperAdmin && showSearch && (
          <div style={{ marginLeft: 8, position: 'relative', display: 'inline-block' }}>
            <Input
              size='small'
              placeholder='Search by email...'
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 250 }}
              autoFocus
              suffix={
                <CloseOutlined
                  style={{ cursor: 'pointer', fontSize: 12 }}
                  onClick={() => {
                    setShowSearch(false);
                    setSearchValue('');
                    setSearchResults([]);
                  }}
                />
              }
            />
            {searchResults.length > 0 && (
              <List
                size='small'
                bordered
                loading={searching}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  width: 350,
                  maxHeight: 200,
                  overflow: 'auto',
                  background: '#fff',
                  zIndex: 1000,
                }}
                dataSource={searchResults}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer', padding: '4px 8px' }}
                    onClick={() => handleSelectUser(item)}
                  >
                    {item.firstName} {item.lastName} ({item.email})
                  </List.Item>
                )}
              />
            )}
          </div>
        )}
      </Styled.UserInfo>
      <TableWrapper>
        <Table columns={LOG_COLUMNS} dataSource={tableLogs} pagination={false} size={'middle'} />
      </TableWrapper>
    </Styled.UserField>
  );
};

export default UserField;
